/**
 * shared/hooks/useCrmConfig.js
 * ----------------------------
 * Reusable hook that fetches the admin-editable CRM workflow config and
 * exposes typed helpers for every dropdown surface. All CRM components
 * should consume this hook instead of hardcoding `STATUS_OPTIONS` /
 * `STATUS_VISUAL` constants.
 *
 *   const cfg = useCrmConfig();
 *   cfg.lead_statuses  → [{id, label, color, order, active, ...}, …]
 *   cfg.tags           → […]
 *   cfg.pipeline_stages.sale → […]
 *   cfg.byId("lead_statuses", "new")  → {id, label, color, …} or null
 *   cfg.defaultId("priorities")       → "p1"
 *
 * Backed by `/api/crm/config`. The hook caches the result in a
 * module-local promise so multiple consumers share one network call.
 * A 60-second sliding TTL keeps it fresh enough for live dropdowns
 * without spamming Mongo (server has its own 30-second cache).
 *
 * v2.71.45 — Initial.
 */

import { useEffect, useState, useCallback, useMemo } from "react";
import api from "../services/api";

/** Module-level cache shared by every <component> using the hook. */
let _cachePromise = null;
let _cacheExpiresAt = 0;
const _CACHE_TTL_MS = 60_000;

/** Fallback config — used while the network call is in flight or when
 *  /api is unreachable. Matches `crm_config.DEFAULTS` (defensive only;
 *  the API call usually wins in <100ms). */
const FALLBACK = {
  lead_statuses: [
    { id: "new", label: "New", color: "#52525B", order: 1, active: true, is_default: true },
    { id: "called", label: "Called", color: "#0044CC", order: 2, active: true },
    { id: "interested", label: "Interested", color: "#92400E", order: 3, active: true },
    { id: "visit", label: "Site Visit", color: "#3730A3", order: 4, active: true },
    { id: "closed", label: "Closed", color: "#065F46", order: 5, active: true },
    { id: "not_interested", label: "Not Interested", color: "#991B1B", order: 6, active: true },
  ],
  visit_statuses: [],
  followup_statuses: [],
  priorities: [],
  tags: [],
  pipeline_stages: { sale: [], rent: [], lease: [] },
  lead_sources: [],
  assignment_categories: [],
};

async function _fetchConfig() {
  try {
    const { data } = await api.get("/crm/config");
    return data || FALLBACK;
  } catch {
    return FALLBACK;
  }
}

/**
 * Force the next consumer to re-fetch. Call this after an admin saves
 * a config edit so dropdowns refresh without a full page reload.
 */
export function invalidateCrmConfigCache() {
  _cachePromise = null;
  _cacheExpiresAt = 0;
}

export function useCrmConfig() {
  const [doc, setDoc] = useState(FALLBACK);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const now = Date.now();
    if (!_cachePromise || now > _cacheExpiresAt) {
      _cachePromise = _fetchConfig();
      _cacheExpiresAt = now + _CACHE_TTL_MS;
    }
    _cachePromise.then((d) => {
      if (cancelled) return;
      setDoc(d || FALLBACK);
      setReady(true);
    });
    return () => { cancelled = true; };
  }, []);

  const byId = useCallback(
    (dimension, id) => {
      if (!id) return null;
      const list = doc[dimension];
      if (Array.isArray(list)) return list.find((x) => x.id === id) || null;
      return null;
    },
    [doc]
  );

  const defaultId = useCallback(
    (dimension) => {
      const list = doc[dimension];
      if (!Array.isArray(list)) return null;
      return (
        list.find((x) => x.is_default && x.active !== false)?.id ||
        list.find((x) => x.active !== false)?.id ||
        null
      );
    },
    [doc]
  );

  const labelFor = useCallback(
    (dimension, id) => byId(dimension, id)?.label || id || "—",
    [byId]
  );

  /** Sorted, active-only items for a dimension — ready to render in a Select. */
  const optionsFor = useCallback(
    (dimension) => {
      const list = doc[dimension];
      if (!Array.isArray(list)) return [];
      return [...list]
        .filter((x) => x.active !== false)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    },
    [doc]
  );

  /** Pipeline stages helper — accepts an intent string. */
  const stagesFor = useCallback(
    (intent) => {
      const all = doc.pipeline_stages || {};
      const list = all[intent] || all.sale || [];
      return [...list]
        .filter((x) => x.active !== false)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    },
    [doc]
  );

  return useMemo(
    () => ({
      ready,
      ...doc,
      byId,
      defaultId,
      labelFor,
      optionsFor,
      stagesFor,
    }),
    [doc, ready, byId, defaultId, labelFor, optionsFor, stagesFor]
  );
}

export default useCrmConfig;
